import { IsEqualComparer, codeSetChanged, drgChanged, getDrgSaveMessage } from 'stores/grouper-store-helper';
import { defineStore } from 'pinia';
import {format} from 'date-fns';
import IodineGrouperService, {InitData} from 'src/services/IodineGrouperService';
import {GrouperService} from 'src/services/GrouperService';
import {computed, ComputedRef, ref, watch, WatchStopHandle} from 'vue';
import {GrouperData} from 'src/contracts/grouperData';
import {addDiagnosisCodes} from 'src/common/codeUtil';
import {Subscription} from 'rxjs';
import {Services} from 'src/services/service-manager';
import {DischargeStatus} from 'src/contracts/discharge-status';
import {isEmpty} from 'src/common/arrayUtil';
import {useLayoutStore} from 'stores/layout-store';
import {CalculateDrgRequestDto} from 'src/contracts/calculateDrgRequestDto';
import {DrgResponseDto} from 'src/contracts/drgResponseDto';
import {DiagnosisCode} from 'src/contracts/diagnosisCode';
import {PatientData} from 'src/contracts/patientData';
import {GrouperInfo} from 'src/contracts/grouperInfo';
import {EditData} from 'src/contracts/edit';
import {useResourcesStore} from 'stores/resources-store';
import {cargoQueue} from 'async';
import { getDiagnosesFromCalculationResult, getProceduresFromCalculationResult, mergeDiagnosisLists, mergeProcedureLists } from 'src/common/code-helper';
import {showNotificationBar, showSuccessSnackBar} from 'src/services/snackbar-service';
import { DraftState, GrouperState } from 'src/contracts/encoderState';
import {setEncoderState} from 'stores/state-helper';
import {Code} from 'src/contracts/code';
import {SubmitOption} from 'components/grouper-common/common-constants';
import {CodeType} from 'src/contracts/codeType';
import {UserSettingsService} from 'src/services/UserSettingsService';
import {useSearchStore} from "stores/search-store";

const cloneDeep = require('lodash.clonedeep');
const mergeWith = require('lodash.mergewith');

interface CalculationArgs {
  calculate_primary: boolean,
  calculate_secondary: boolean
}

export const useGrouperStore = defineStore('grouperstore', () => {

  const resourcesStore = useResourcesStore();

  // changed to cargo queue so that we can group calculation requests together for slow internet users with rapid fire clicking
  const calculateQueue = cargoQueue((tasks: CalculationArgs[], callback) => {
    tasks = tasks || [];

    const calculationArgs: CalculationArgs = {
      calculate_primary: tasks.some(x => x.calculate_primary),
      calculate_secondary: tasks.some(x => x.calculate_secondary)
    };

    calculateDrg(calculationArgs).then(() => callback()).catch(callback);
  }, 1);


  const loaded = ref<boolean>(false);
  const loadingTimeout = ref<boolean>(false);
  const readOnly = ref<boolean>(false);
  const queueEmpty = ref(verifyQueue());
  const grouperInfo= ref<GrouperInfo>()
  const dualGrouping = ref<boolean>(false);
  const resequence = ref<boolean>(false);
  const drgDrawerState = ref<boolean>(false);
  const findAll = ref<boolean>(false);
  const diagnosisCodes = ref<DiagnosisCode[]>([]);
  const procedureCodes = ref<Code[]>([]);
  const getCodesCount = computed(() => diagnosisCodes.value.length + procedureCodes.value.length);
  const edits = ref<EditData[]>([]);
  const isPrimaryDrgMs = computed(() => grouperInfo?.value?.grouper_source?.grouper_type == 'CMS');
  const primaryDrg = ref<DrgResponseDto>({});
  const secondaryDrg = ref<DrgResponseDto>({});
  const submitDrgTypes = ref<SubmitOption[]>([]);
  const showDraftBanner = ref(false);

  const editsSelectedCode = ref<string | undefined>();

  const patient = ref<PatientData>();
  let cancelDischargeWatch: WatchStopHandle;
  const dischargeStatuses = ref<DischargeStatus[]>([]);
  const getDischargeStatuses = computed(() => dischargeStatuses.value);
  const dischargeStatus = ref<DischargeStatus | undefined>();
  const patientEffectiveDate = ref<string>(format(new Date(), 'yyyy-MM-dd'));
  const aprCalculationAvailable = computed(() => !readOnly.value && diagnosisCodes.value.length > 0 && aprRecalculationNeeded.value);
  const calculatingApr = ref(false);
  const isCalculatingApr = computed(() => calculatingApr.value);
  const aprRecalculationNeeded = ref(false);
  const submittingDrgs = ref(false);
  const isSubmittingDrgs = computed(() => submittingDrgs.value);
  const isDirty = computed(() => drgChanged(oldPrimaryDrg.value, primaryDrg.value)
    || drgChanged(oldSecondaryDrg.value, secondaryDrg.value)
    || codeSetChanged(diagnosisCodes.value, oldDiagnoses.value, dxPoaComparer)
    || codeSetChanged(procedureCodes.value, oldProcedures.value));

  const canSubmitDrg = computed(() =>
    isDirty.value // don't allow submission if nothing changed.
    && !aprCalcNeededForSubmit.value // don't allow submission if we still need to submit APR calc
    && !isSubmittingDrgs.value // don't allow submission if we have a submission in progress already
    && !isCalculatingApr.value // don't allow submission if we are currently calculating an APR
  );

  const lastSubmittedTypes = ref<string[]>([]);
  const getLastSubmittedTypes = computed(() => lastSubmittedTypes.value);

  const dxPoaComparer: IsEqualComparer = (a: Code, b: Code) => {
    return (<DiagnosisCode>a)?.present_on_admission == (<DiagnosisCode>b)?.present_on_admission;
  }

  const aprCalcNeededForSubmit: ComputedRef<boolean> = computed(() =>
    <boolean>(dualGrouping.value && aprRecalculationNeeded.value && (!isPrimaryDrgMs.value || !!secondaryDrg?.value?.drg)));

  const oldPrimaryDrg = ref<DrgResponseDto>({});
  const oldSecondaryDrg = ref<DrgResponseDto>({});
  const oldDiagnoses = ref<DiagnosisCode[]>([])
  const oldProcedures = ref<Code[]>([]);
  const currentInitData = ref<InitData>();
  const resetButtonEnabled = ref<boolean>(false);

  function resetDRGData() {
    diagnosisCodes.value = [];
    procedureCodes.value = [];
    lastSubmittedTypes.value = [];
    showDraftBanner.value = false;
    readOnly.value = false;
  }

  calculateQueue.empty(() => {
    recalculateQueue();
  });

  function verifyQueue() {
    return calculateQueue.length() == 0;
  }

  function recalculateQueue() {
    queueEmpty.value = verifyQueue();
  }

  function recalculateNeeded(calculateAll = false) {

    if (!calculateAll) {
      aprRecalculationNeeded.value = true;
    }

    // automatically recalc MS unless calc all is true and then recalc everything
    calculateQueue.push({
      calculate_primary: calculateAll || !!isPrimaryDrgMs?.value,
      calculate_secondary: calculateAll || !isPrimaryDrgMs?.value
    });

    recalculateQueue();

    // when we need to recalculate, something has changed so get the updated research
  }

  async function addDiagnosisCode(code: string, description?: string, poa?: string, snackbar = false): Promise<void> {

    if (diagnosisCodes.value.find((dx) => dx.code.toLowerCase() == code.toLowerCase())) {
      return;
    }

    if (!description) {
      const searchResult = await useSearchStore().quickAddCodeSearch(code, CodeType.Diagnosis);
      description = searchResult?.mediumDescription || searchResult?.shortDescription || '';
    }

    diagnosisCodes.value.push({
      pdx: false,
      drgModifier: [],
      present_on_admission: poa || undefined,
      quality: [],
      risk: [],
      id: diagnosisCodes.value.length,
      action: '',
      code,
      description: description || undefined
    });

    if (snackbar) {
      showCodeAddSnackbar(CodeType.Diagnosis, code);
    }

    recalculateNeeded();
  }

  function showCodeAddSnackbar(codeType: CodeType, code: string) {
    showSuccessSnackBar(`${code} has been added!`, [{
      label: 'Undo',
      handler: () => {
        removeCode(codeType, code);
      }
    }], 2000);
  }

  async function changeResequence() {
    resequence.value = !resequence.value
    const userSettingsService = Services.service(UserSettingsService);
    await userSettingsService.setUserPreference('grouperResequence', `${resequence.value}`);
    await recalculateNeeded()
    const notificationText = resequence.value ? 'Auto Sequence has been enabled.'
        : 'Auto Sequence has been disabled. You are now permitted to drag and drop codes.';
    showNotificationBar(notificationText)
  }

  async function changeDrgDrawerState(state: boolean ) {
    drgDrawerState.value = state;
    const userSettingsService = Services.service(UserSettingsService);
    await userSettingsService.setUserPreference('grouperDrgDrawerState', `${drgDrawerState.value}`);
  }

  async function addProcedureCode(code: string, description?: string, snackbar = false): Promise<void> {

    if (!description) {
      const searchResult = await useSearchStore().quickAddCodeSearch(code, CodeType.Procedure);
      description = searchResult?.mediumDescription || searchResult?.shortDescription || '';
    }
    procedureCodes.value?.push({
      action: '',
      code,
      id: procedureCodes.value?.length || 0,
      description: description || undefined
    });

    if (snackbar) {
      showCodeAddSnackbar(CodeType.Procedure, code);
    }

    recalculateNeeded()
  }

  function removeCode(codeType: CodeType, code: string): void {
    if (!code) {
      return;
    }
    const codes = codeType == CodeType.Procedure ? procedureCodes : diagnosisCodes;
    const index = codes.value.findIndex((c) => c.code.toLowerCase() == code.toLowerCase());
    if (index != -1) {
      codes.value.splice(index, 1);
      recalculateNeeded();
    }
  }

  async function calculateDrg(calcArgs: CalculationArgs): Promise<void> {
    const grouperData: CalculateDrgRequestDto = {
      diagnoses: diagnosisCodes.value,
      procedures: procedureCodes.value,
      calculate_primary: calcArgs.calculate_primary,
      calculate_secondary: calcArgs.calculate_secondary,
      resequence: resequence.value,
      discharge_status: dischargeStatus.value?.code
    };

    const grouperService = Services.service(GrouperService);
    const drgResponseDtos = await grouperService.calculateDrg(grouperData);

    diagnosisCodes.value =
      mergeDiagnosisLists(
        diagnosisCodes.value,
        getDiagnosesFromCalculationResult(drgResponseDtos, isPrimaryDrgMs.value),
        resequence.value,
        calcArgs.calculate_primary);
    procedureCodes.value = mergeProcedureLists(procedureCodes.value, getProceduresFromCalculationResult(drgResponseDtos), calcArgs.calculate_primary);

    if (calcArgs.calculate_primary) handleDrgEdits(drgResponseDtos);
    updateDrgData(drgResponseDtos, calcArgs.calculate_primary, calcArgs.calculate_secondary);

    // if our calculate request succeeded, save info as draft
    await grouperService.saveDraft(getGrouperState());
    resetButtonEnabled.value = true;

    await useResourcesStore().getMsDrgAnalysis();

    if (calcArgs.calculate_primary && calcArgs.calculate_secondary) {
      aprRecalculationNeeded.value = false;
    }
  }

  function calculateApr(): void {

    aprRecalculationNeeded.value = false;
    calculatingApr.value = true;
    calculateQueue.push({
      calculate_primary: true,
      calculate_secondary: true
    }, (err) => {
      calculatingApr.value = false;
      if (err) {
        console.error(err);
        aprRecalculationNeeded.value = true;
      }
    });

    recalculateQueue();
  }

  async function saveDrg(): Promise<void> {

    lastSubmittedTypes.value = [];
    const types = submitDrgTypes?.value?.map(x => x.label);
    if (isEmpty(types)) {
      return;
    }

    submittingDrgs.value = true;
    const drgs: DrgResponseDto[] = [cloneDeep(primaryDrg.value)];
    if (dualGrouping.value) {
      const secondary = <DrgResponseDto>cloneDeep(secondaryDrg.value);
      secondary.grouper_type ??= isPrimaryDrgMs.value ? 'APR' : 'CMS';
      drgs.push(secondary);
    }

    drgs.forEach(drg => drg.payload_ready = !!drg.drg);

    const dxCopy = cloneDeep(diagnosisCodes.value);
    const procCopy = cloneDeep(procedureCodes.value);
    const grouperData: any  = {
      diagnoses: dxCopy,
      procedures: (<Code[]>procCopy).map(procedure => {
        if (!procedure.position) {
          procedure.position = procedure.id;
        }
        return procedure;
      }),
      grouper_state: types.map(type => type.toUpperCase()),
      drg_info: drgs
    };

    const grouperService = Services.service(GrouperService);
    try {
      await grouperService.saveDrg(grouperData);

      oldPrimaryDrg.value = drgs[0];
      if (drgs.length == 2) {
        oldSecondaryDrg.value = drgs[1];
      }

      oldDiagnoses.value = dxCopy;
      oldProcedures.value = procCopy;

      lastSubmittedTypes.value = types;
      showSuccessSnackBar(getDrgSaveMessage(drgs, types));
      showDraftBanner.value = false;
      resetButtonEnabled.value = false;
    } finally {
      submittingDrgs.value = false;
      const refreshUIEvent = new Event('refreshUI');
      window.dispatchEvent(refreshUIEvent);
    }
  }

  let setVisitContextSubscription: Subscription | undefined;
  let addCodeSubscription: Subscription | undefined;

  function createAddCodeSubscription() {
    addCodeSubscription = IodineGrouperService.addCodes$.subscribe(
      async (addCodeData: GrouperData) => {

        if (!isEmpty(addCodeData?.diagnosisCodes)) {
          addDiagnosisCodes(diagnosisCodes.value, addCodeData.diagnosisCodes);
        }
        if (!isEmpty(addCodeData?.procedureCodes)) {
          addCodeData.procedureCodes?.forEach(procedure => {
            addProcedureCode(procedure.code, procedure.description || '');
          });
        }
        recalculateNeeded(true);
      }
    );
  }

  async function changeFindAllSearch() {
    findAll.value = !findAll.value
    const userSettingsService = Services.service(UserSettingsService);
    await userSettingsService.setUserPreference('findAllSearch', `${findAll.value}`);
  }

  async function reloadVisit(ignoreDraft = false) {
    if (currentInitData.value) {
      await loadVisit(currentInitData.value, ignoreDraft);
    }
  }

  async function loadVisit(initData: InitData, ignoreDraft = false) {
    loaded.value = false;
    loadingTimeout.value = false;
    const timeoutId = setTimeout(() => {
      loadingTimeout.value = true;
    }, 15000);
    resetDRGData();
    useLayoutStore().setResourcesSelected(false);
    useLayoutStore().changeMode(initData.mode, false);
    Services.setDefaultConfig(initData.endpoint, initData.visitId, initData.facilityCode);
    dualGrouping.value = initData.dualGrouping;

    const grouperService = Services.service(GrouperService);
    const userSettingsService = Services.service(UserSettingsService);
    // load discharge
    if (isEmpty(dischargeStatuses.value)) {
      try {
        dischargeStatuses.value = await grouperService.getDischargeStatuses()
      } catch (ex) {
        console.error(ex);
      }
    }

    const resequenceValue = await userSettingsService.getUserPreference('grouperResequence', 'false');
    resequence.value = !!resequenceValue;

    const drgDrawerStateValue = await userSettingsService.getUserPreference('grouperDrgDrawerState', 'false');
    drgDrawerState.value = !!drgDrawerStateValue;

    const findAllValue = await userSettingsService.getUserPreference('findAllSearch', 'false');
    findAll.value = !!findAllValue;

    const response = await grouperService.getGrouperData();
    resetButtonEnabled.value = !!response.draft_data && !ignoreDraft;
    readOnly.value = response.readOnly??false;

    if (initData?.state && !ignoreDraft) {
      setEncoderState(initData.state);
    } else {
      if (response.draft_data && !ignoreDraft) {
        setDraftState(response.draft_data);
        showDraftBanner.value = !readOnly.value;
      } else {
        updateDrgData(response.drgInfo, true, true);
        diagnosisCodes.value = response.diagnosisCodes || [];
        procedureCodes.value = response.procedureCodes || [];
        // store last submitted drg's separately, so we can track changes
        oldPrimaryDrg.value = cloneDeep(primaryDrg.value);
        oldSecondaryDrg.value = cloneDeep(secondaryDrg.value);
        oldDiagnoses.value = cloneDeep(diagnosisCodes.value);
        oldProcedures.value = cloneDeep(procedureCodes.value);
        
        if (response?.drgInfo?.[0]?.discharge_status) {
          const status = dischargeStatuses.value.find((status) => status.code === response.drgInfo[0].discharge_status);
          dischargeStatus.value = status;
        }
      }

      // we still keep the patient down here even if loading from draft in case
      // something with the patient changed via hL7 (like name or discharge status)
      patient.value = response.patient;
      setPatientEffectiveDate();
      grouperInfo.value = response.grouperInfo;
    }

    resourcesStore.getMsDrgAnalysis();
    cancelDischargeWatch = watch(dischargeStatus, () => recalculateNeeded());
    loaded.value = true;
    clearTimeout(timeoutId);
    loadingTimeout.value = false;
  }

  function createContextSubscription() {
    setVisitContextSubscription = IodineGrouperService.setVisitContext$.subscribe(
      async (initData: InitData) => {
        currentInitData.value = initData;
        await loadVisit(initData);
      }
    );
  }

  function handleDrgEdits(drgResponseDto: DrgResponseDto[] | undefined): void {
    // reset edits
    edits.value = [];
    // reset edits on codes
    diagnosisCodes?.value?.forEach(dx => {
      dx.edit = undefined;
    });
    procedureCodes?.value?.forEach(dx => {
      dx.edit = undefined;
    });


    drgResponseDto?.forEach(drg => {
      drg.edits?.edit.forEach(edit => {
        edits.value.push(edit);

        const index = parseInt(edit.position || '-1');
        switch (edit.content_type) {
          case 'diagnoses':
            const diagnosisCode = diagnosisCodes.value?.at(index);
            if (diagnosisCode) {
              diagnosisCode.edit = edit;
            }
            break;
          case 'procedures':
            const procedureCode = procedureCodes.value?.at(index);
            if (procedureCode) {
              procedureCode.edit = edit;
            }
            break;
          default:
            console.log(`Unexpected content type: ${edit.content_type}`);
            break;
        }
      })
    })
  }


  function updateDrgData(drgResponseDto: DrgResponseDto[], primaryCalculated: boolean, secondaryCalculated: boolean): void {
    if (isEmpty(drgResponseDto)) {
      primaryDrg.value = {};
      secondaryDrg.value = {};
      return;
    }

    if (primaryCalculated) {
      primaryDrg.value = drgResponseDto.find(drg => drg?.priority == 'PRIMARY') || {};
    }

    if (secondaryCalculated) {
      secondaryDrg.value = drgResponseDto.find(drg => drg?.priority != 'PRIMARY') || {};
    }
  }

  function setAllPoaYes() {
    const diagnosisToUpdate = diagnosisCodes?.value?.filter(dx => dx.present_on_admission?.trim() === '' || dx.present_on_admission === undefined);
    if (diagnosisToUpdate.length > 0) {
      diagnosisToUpdate.forEach(dxCode => {
        dxCode.present_on_admission = 'Y';
      });
      recalculateNeeded();

      showSuccessSnackBar(poaUpdateSnackbarText(diagnosisToUpdate.length), [], 2000);
    }
  }

  function poaUpdateSnackbarText(diagnosesLength: number) {
    if (diagnosesLength > 1) {
      return`(${diagnosesLength}) POA’s without a selection, have been updated to Y - Yes`
    }

    return`(${diagnosesLength}) POA without a selection, have been updated to Y - Yes`
  }

  function setPatientEffectiveDate(){
    if (patient.value && patient.value['dischargeDate']){
      const dischargeDateFormatArray = patient.value['dischargeDate'].match(/^\d{4}-\d{2}-\d{2}$/);
      if (dischargeDateFormatArray && dischargeDateFormatArray.length > 0) {
        patientEffectiveDate.value = patient.value['dischargeDate'];
      } else {
        patientEffectiveDate.value = format(new Date, 'yyyy-MM-dd');
      }
    }
  }

  function $onMount() {
    createContextSubscription();
    createAddCodeSubscription();
  }

  function $reset() {
    loaded.value = false;
    setVisitContextSubscription && setVisitContextSubscription.unsubscribe();
    addCodeSubscription && addCodeSubscription.unsubscribe();
    cancelDischargeWatch && cancelDischargeWatch();
  }

  function changeSelectedCode(code: string) {
    editsSelectedCode.value = code;
  }

  function getGrouperState(): GrouperState {
    return mergeWith({
      patient: <PatientData>patient.value,
      grouperInfo: <GrouperInfo>grouperInfo.value,
      dualGrouping: dualGrouping.value,
      resequence: resequence.value,
      findAll: findAll.value,
      readOnly: readOnly.value,
      submitDrgTypes: submitDrgTypes.value,
      showDraftBanner: showDraftBanner.value,
    }, getDraftState());
  }

  function setGrouperState(grouperState: GrouperState): void {
    patient.value = grouperState?.patient;
    grouperInfo.value = grouperState?.grouperInfo;
    dualGrouping.value = grouperState?.dualGrouping;
    resequence.value = grouperState?.resequence;
    readOnly.value = grouperState?.readOnly;
    findAll.value = grouperState?.findAll;
    submitDrgTypes.value = grouperState?.submitDrgTypes;
    showDraftBanner.value = grouperState?.showDraftBanner;
    setDraftState(grouperState);
  }

  function getDraftState(): DraftState {
    return {
      diagnosisCodes: diagnosisCodes.value,
      procedureCodes: procedureCodes.value,
      edits: edits.value,
      primaryDrg: primaryDrg.value,
      secondaryDrg: secondaryDrg.value,
      dischargeStatus: <DischargeStatus>dischargeStatus.value,
      aprRecalculationNeeded: aprRecalculationNeeded.value,
      oldPrimaryDrg: <DrgResponseDto>oldPrimaryDrg.value,
      oldSecondaryDrg: <DrgResponseDto>oldSecondaryDrg.value,
      oldDiagnoses: oldDiagnoses.value,
      oldProcedures: oldProcedures.value,
    };
  }

  function setDraftState(grouperState: DraftState): void {
    diagnosisCodes.value = grouperState?.diagnosisCodes;
    procedureCodes.value = grouperState?.procedureCodes
    edits.value = grouperState?.edits;
    primaryDrg.value = grouperState?.primaryDrg;
    secondaryDrg.value = grouperState?.secondaryDrg;
    dischargeStatus.value = grouperState?.dischargeStatus;
    aprRecalculationNeeded.value = grouperState?.aprRecalculationNeeded;
    oldPrimaryDrg.value = grouperState?.oldPrimaryDrg;
    oldSecondaryDrg.value = grouperState?.oldSecondaryDrg;
    oldDiagnoses.value = grouperState?.oldDiagnoses;
    oldProcedures.value = grouperState?.oldProcedures;
  }

  return {
    loaded,
    loadingTimeout,
    readOnly,
    diagnosisCodes,
    getCodesCount,
    procedureCodes,
    edits,
    resequence,
    drgDrawerState,
    findAll,
    primaryDRG: primaryDrg,
    secondaryDRG: secondaryDrg,
    patient,
    aprCalculationAvailable,
    aprRecalculationNeeded,
    addDiagnosisCode,
    addProcedureCode,
    editsSelectedCode,
    changeSelectedCode,
    $reset,
    $onMount,
    setVisitContextSubscription,
    addCodeSubscription,
    calculateApr,
    saveDrg,
    changeResequence,
    changeDrgDrawerState,
    changeFindAllSearch,
    getDischargeStatuses,
    dischargeStatus,
    patientEffectiveDate,
    isPrimaryDrgMs,
    recalculateNeeded,
    isCalculatingApr,
    setAllPoaYes,
    canSubmitDrg,
    aprCalcNeededForSubmit,
    queueEmpty,
    getGrouperState,
    setGrouperState,
    submitDrgTypes,
    showDraftBanner,
    isDirty,
    getLastSubmittedTypes,
    dualGrouping,
    reloadVisit,
    resetButtonEnabled,
    getDraftState,
    setDraftState,
    oldPrimaryDrg
  }
})
